import React from "react";
import styled from "styled-components";

const Page = styled.div`
  min-height: 100vh;
  padding: 118px 20px 80px;
  background:
    radial-gradient(860px at 12% 0%, rgba(245, 158, 11, 0.12), transparent 58%),
    radial-gradient(900px at 88% 6%, rgba(56, 189, 248, 0.12), transparent 60%),
    linear-gradient(180deg, #06101d 0%, #040814 100%);
`;

const Wrap = styled.div`
  max-width: 920px;
  margin: 0 auto;
`;

const Shell = styled.div`
  padding: 30px;
  border-radius: 30px;
  background: linear-gradient(180deg, rgba(8, 16, 29, 0.96), rgba(5, 11, 22, 0.92));
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.24);
`;

const Eyebrow = styled.div`
  display: inline-flex;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.16);
  color: #7dd3fc;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin: 18px 0 10px;
  color: #f8fafc;
  font-size: clamp(2rem, 4vw, 3.3rem);
  line-height: 1.06;
`;

const Lead = styled.p`
  margin: 0;
  color: #94a3b8;
  font-size: 15px;
  line-height: 1.8;
`;

const Section = styled.section`
  margin-top: 28px;
  padding-top: 24px;
  border-top: 1px solid rgba(148, 163, 184, 0.12);
`;

const SectionTitle = styled.h2`
  margin: 0 0 12px;
  color: #f8fafc;
  font-size: 1.3rem;
`;

const Text = styled.p`
  margin: 0;
  color: #cbd5e1;
  font-size: 14px;
  line-height: 1.85;
`;

const List = styled.ul`
  margin: 12px 0 0;
  padding-left: 20px;
  color: #cbd5e1;

  li {
    margin-bottom: 10px;
    line-height: 1.8;
  }
`;

export default function PrivacyPolicyPage() {
  return (
    <Page>
      <Wrap>
        <Shell>
          <Eyebrow>Privacy Policy</Eyebrow>
          <Title>Privacy and account data at skilll.dev</Title>
          <Lead>
            Last updated: April 5, 2026. This page explains what information we
            collect to run skilll.dev, how we use it, and what browser storage is
            used for core product functionality.
          </Lead>

          <Section>
            <SectionTitle>What we collect</SectionTitle>
            <List>
              <li>Account details such as name, email address, phone number, and auth provider.</li>
              <li>Roadmap, progress, completed days, settings, saved notes, and related product data you create inside the app.</li>
              <li>Technical information needed to operate the service, such as authentication state and theme preference.</li>
            </List>
          </Section>

          <Section>
            <SectionTitle>How we use your information</SectionTitle>
            <List>
              <li>To create and maintain your account.</li>
              <li>To generate and store your roadmap, progress, reminders, notes, and related learning workflow data.</li>
              <li>To send essential account emails such as welcome, password reset, and roadmap-related transactional emails.</li>
              <li>To keep the app functional, secure, and personalized for your current session and preferences.</li>
            </List>
          </Section>

          <Section>
            <SectionTitle>Browser storage we currently use</SectionTitle>
            <Text>
              skilll.dev currently uses browser storage for core product functions.
              This includes local storage for auth-related client state and theme
              preference. These are used to keep you signed in where applicable,
              maintain your app experience, and remember basic settings.
            </Text>
          </Section>

          <Section>
            <SectionTitle>Cookies and similar technologies</SectionTitle>
            <Text>
              We do not currently rely on advertising or retargeting cookies in
              the main app flow based on the current codebase. If analytics,
              advertising, or non-essential tracking technologies are added later,
              this policy and our cookie disclosures should be updated before they
              go live.
            </Text>
          </Section>

          <Section>
            <SectionTitle>Third-party services</SectionTitle>
            <List>
              <li>Firebase is used for authentication, database, and cloud functions.</li>
              <li>Resend is used for transactional email delivery.</li>
              <li>Google sign-in may be used when you choose Google authentication.</li>
            </List>
          </Section>

          <Section>
            <SectionTitle>Data retention</SectionTitle>
            <Text>
              We retain account and product data for as long as needed to provide
              the service, meet security needs, or comply with legal obligations,
              unless you request deletion where applicable.
            </Text>
          </Section>

          <Section>
            <SectionTitle>Your choices</SectionTitle>
            <List>
              <li>You can update profile information from your account where supported.</li>
              <li>You can request password reset through the login flow.</li>
              <li>You can contact us if you want to ask about account deletion or privacy questions.</li>
            </List>
          </Section>

          <Section>
            <SectionTitle>Contact</SectionTitle>
            <Text>
              For privacy questions related to skilll.dev, use your official support
              or company contact email and keep this page updated with the correct
              contact route before launch.
            </Text>
          </Section>
        </Shell>
      </Wrap>
    </Page>
  );
}
